import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Gift } from "lucide-react";
import { AuthLayout } from "./Login";
import api, { formatApiError } from "@/lib/api";
import { useTr } from "@/i18n/translator";
import PasswordInput from "@/components/PasswordInput";

export default function Register() {
  const tr = useTr();
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromQuery = params.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(refFromQuery.toUpperCase());
  const [showRef, setShowRef] = useState(!!refFromQuery);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refFromQuery) {
      setReferralCode(refFromQuery.toUpperCase());
      setShowRef(true);
    }
  }, [refFromQuery]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!ageConfirmed) {
      setErr(tr("Debes confirmar que eres mayor de 18 años."));
      return;
    }
    if (!termsAccepted) {
      setErr(tr("Debes aceptar los Términos de Servicio y la Política de Privacidad."));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        name,
        email,
        password,
        referral_code: referralCode?.trim() || null,
      });
      // After register, log in to set context
      const r = await login(email, password);
      setLoading(false);
      if (r.ok) nav("/onboarding");
      else setErr(r.error);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={tr("Solicitud de acceso")} subtitle={tr("CREA TU CUENTA CIA MARKETING®")}>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        {refFromQuery && (
          <div className="border border-[#E53935] bg-[#FFD600] px-4 py-3 flex items-center gap-3" data-testid="register-ref-banner">
            <Gift className="w-5 h-5" />
            <div className="font-mono-data text-sm">
              Vas a activar <b>{tr("30 días Pro gratis")}</b> {tr("con el código")} <b>{referralCode}</b>.
            </div>
          </div>
        )}
        <label className="block">
          <span className="font-mono-mark text-[10px] text-[#5C5C5C]">NOMBRE / MARCA</span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
            placeholder={tr("Mi Empresa SA")}
            data-testid="register-name"
          />
        </label>
        <label className="block">
          <span className="font-mono-mark text-[10px] text-[#5C5C5C]">EMAIL</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
            placeholder={tr("tu@empresa.com")}
            data-testid="register-email"
          />
        </label>
        <label className="block">
          <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CONTRASEÑA (MÍN 6)")}</span>
          <div className="mt-2">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              testid="register-password"
            />
          </div>
        </label>

        {showRef ? (
          <label className="block">
            <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CÓDIGO DE REFERIDO (OPCIONAL)")}</span>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="CIA-XXXXXX"
              className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm uppercase"
              data-testid="register-referral"
            />
          </label>
        ) : (
          <button type="button" onClick={() => setShowRef(true)} className="font-mono-mark text-[10px] text-[#E53935] underline decoration-[#E53935]" data-testid="register-show-ref">
            {tr("¿TIENES CÓDIGO DE REFERIDO?")}
          </button>
        )}

        {err && (
          <div className="border border-[#E53935] bg-[#E53935]/10 px-4 py-3 font-mono-data text-sm text-[#E53935]" data-testid="register-error">
            {err}
          </div>
        )}

        <div className="space-y-2 pt-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              className="mt-1"
              data-testid="register-age-check"
            />
            <span className="font-mono-data text-xs text-[#5C5C5C]">
              {tr("Confirmo que tengo al menos 18 años (o la edad legal de mayoría en mi jurisdicción).")}
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1"
              data-testid="register-terms-check"
            />
            <span className="font-mono-data text-xs text-[#5C5C5C]">
              {tr("Acepto los")}{" "}
              <Link to="/legal/terms" target="_blank" className="underline text-[#0A0A0A]">{tr("Términos de Servicio")}</Link>,{" "}
              <Link to="/legal/privacy" target="_blank" className="underline text-[#0A0A0A]">{tr("Política de Privacidad")}</Link>{" "}
              {tr("y")}{" "}
              <Link to="/legal/cookies" target="_blank" className="underline text-[#0A0A0A]">{tr("Política de Cookies")}</Link>.
            </span>
          </label>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#E53935] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#E53935] flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="register-submit"
        >
          {loading ? tr("REGISTRANDO…") : tr("CREAR CUENTA")}
          {!loading && <ArrowRight className="w-4 h-4" />}
        </button>
        <div className="text-center font-mono-data text-sm">
          {tr("¿Ya tienes cuenta?")}{" "}
          <Link to="/login" className="underline decoration-[#E53935] underline-offset-4" data-testid="register-go-login">
            {tr("Inicia sesión")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
